import { listDeploymentsByTimeRange, type DeploymentRecord } from './deploymentService';

/** How far back deploy history is read to work out which image each client runs. */
export const LIVE_LOOKBACK_DAYS = 120;
const PAGE_SIZE = 200;
const MAX_PAGES = 15;
const CACHE_KEY = 'csp-live-images-v1';
const CACHE_MS = 10 * 60 * 1000;

export const liveImageKey = (repository: string | undefined, tag: string) => `${repository || 'numa-deploy'}#${tag}`;

export interface LiveImages {
  /** Image key (`liveImageKey`) to the clients that run it, sorted by name. */
  clientsByImage: Map<string, string[]>;
  /** Client to the newest successful deploy that put its current image there. */
  deployByClient: Map<string, DeploymentRecord>;
}

interface CachedLive {
  at: number;
  deploys: DeploymentRecord[];
}

function build(deploys: DeploymentRecord[]): LiveImages {
  const deployByClient = new Map<string, DeploymentRecord>();
  for (const dep of deploys) deployByClient.set(dep.clientName, dep);
  const clientsByImage = new Map<string, string[]>();
  for (const [client, dep] of deployByClient) {
    const key = liveImageKey(dep.repository, dep.imageTag!);
    clientsByImage.set(key, [...(clientsByImage.get(key) || []), client]);
  }
  for (const list of clientsByImage.values()) list.sort();
  return { clientsByImage, deployByClient };
}

/**
 * Which image each client runs: the image of its newest successful deploy in
 * the lookback window. A plan changes nothing and a group summary row is not a
 * deploy, so both are left out.
 *
 * The read is several pages of deploy history, so the answer is kept for ten
 * minutes in this browser tab.
 */
export async function loadLiveImages(force = false): Promise<LiveImages> {
  if (!force) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null') as CachedLive | null;
      if (cached && Date.now() - cached.at < CACHE_MS) return build(cached.deploys);
    } catch {
      // A cache that will not parse is ignored.
    }
  }

  const from = new Date(Date.now() - LIVE_LOOKBACK_DAYS * 24 * 60 * 60 * 1000).toISOString();
  const newest = new Map<string, DeploymentRecord>();
  let cursor: Record<string, unknown> | undefined;
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await listDeploymentsByTimeRange({ fromIso: from, pageSize: PAGE_SIZE, exclusiveStartKey: cursor });
    for (const dep of res.items) {
      if (dep.entityType === 'group' || dep.mode === 'plan') continue;
      if ((dep.status || '').toLowerCase() !== 'success') continue;
      if (!dep.clientName || !dep.imageTag) continue;
      // Pages arrive newest first, so the first success seen for a client is its current image.
      if (!newest.has(dep.clientName)) newest.set(dep.clientName, dep);
    }
    cursor = res.lastEvaluatedKey;
    if (!cursor) break;
  }

  // Keep only what the pages need: a whole record carries long ARNs and messages.
  const deploys = [...newest.values()].map((dep) => ({
    deploymentId: dep.deploymentId,
    clientName: dep.clientName,
    imageTag: dep.imageTag,
    repository: dep.repository,
    startedAt: dep.startedAt,
    endedAt: dep.endedAt,
    deploymentLabel: dep.deploymentLabel,
  })) as DeploymentRecord[];
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), deploys } satisfies CachedLive));
  } catch {
    // A full session store only costs the cache.
  }
  return build(deploys);
}
